import React, { useEffect } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import BTNLoader from "../../../lib/components/BTNLoader";
import marketplaceService from "../../../service/marketplaceService";
import { REVOKE_ADDON_VALIDATION } from "../../../validations/marketplace";
import errorHandler from "../../../utils/errorHandler";

/**
 * B7 — Revoke an add-on immediately or at period end (audit reason required).
 */
const RevokeAddonModal = ({ isOpen, onClose, onSuccess, addon }) => {
  const formHook = useForm({
    resolver: yupResolver(REVOKE_ADDON_VALIDATION),
    mode: "all",
    defaultValues: { reason: "", atPeriodEnd: false },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formHook;

  useEffect(() => {
    if (!isOpen) return;
    reset({ reason: "", atPeriodEnd: false });
  }, [isOpen, reset]);

  const mutation = useMutation({
    mutationFn: (values) =>
      marketplaceService.revokeAddon(addon.id || addon._id || addon.addonId, {
        reason: values.reason,
        atPeriodEnd: Boolean(values.atPeriodEnd),
      }),
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      toast.success("Add-on revoked");
      onSuccess?.();
      onClose?.();
    },
  });

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit((values) => mutation.mutate(values)),
      }}
    >
      <div className="d-flex justify-content-between align-items-center gap-3 p-3 pe-2">
        <div>
          <div className="fs-5 fw-medium">Revoke Add-on</div>
          <div className="text-secondary fs-sm">
            {addon?.name || addon?.itemCode || addon?.code || "Add-on"}
          </div>
        </div>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </div>
      <Divider />

      <DialogContent>
        <div className="mb-3">
          <Controller
            name="atPeriodEnd"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Revoke at period end (keep access until renewal)"
              />
            )}
          />
        </div>
        <div>
          <label className="d-block mb-1">
            Reason <span className="text-danger">*</span>
          </label>
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                minRows={2}
                placeholder="e.g. Customer cancelled / support correction"
                error={!!errors.reason}
                helperText={errors.reason?.message}
              />
            )}
          />
        </div>
      </DialogContent>

      <DialogActions className="px-3 pb-3">
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="error"
          disabled={mutation.isPending}
          startIcon={<BTNLoader isLoading={mutation.isPending} />}
        >
          Revoke
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevokeAddonModal;
